import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';

const nav = [
  { to: '/admin', label: 'Users', end: true },
  { to: '/admin/role-compute-presets', label: 'Role analysis rules' },
  { to: '/admin/role-layers', label: 'Layer tags' },
  { to: '/admin/usage', label: 'Tool usage' },
  { to: '/admin/vegetation', label: 'Vegetation' },
  { to: '/admin/forecast', label: 'Forecast' },
  { to: '/admin/pest', label: 'Pest risk' },
  { to: '/admin/whats-new', label: "What's New" },
] as const;

type Props = {
  title: string;
  children: ReactNode;
  /** extra actions next to "Back to map" */
  headerRight?: ReactNode;
};

/**
 * Shared shell for all /admin/* pages: top nav + title + back to map.
 */
function BackToMapButton() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/')}
      className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50"
    >
      Back to map
    </button>
  );
}

export default function AdminLayout({ title, children, headerRight }: Props) {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-gray-100">
            <h1 className="text-lg font-bold text-gray-900">{title}</h1>
            <div className="flex flex-wrap items-center gap-2">
              {headerRight}
              <BackToMapButton />
            </div>
          </div>
          <nav className="flex flex-wrap gap-1 py-2" aria-label="Admin sections">
            {nav.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">{children}</div>
    </div>
  );
}
